/* eslint-disable ember/no-computed-properties-in-native-classes */
import { action, computed } from '@ember/object';
import { getOwner } from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismButton, PrismSelectInput } from '@mylighthouse/prism-ember';
import { or } from 'ember-truth-helpers';
import {
  OTA_IDS,
  PLATFORMS,
} from 'frontend-utils/utils/constants/constant-values';
import Countries from 'frontend-utils/utils/countries';
import GeneralModalFilterBar from 'lumen/components/general/modal-filter-bar';
import HotelStrategyChartsChartEvolution from 'lumen/components/hotel/strategy/charts/chart-evolution';
import {
  MEALTYPE_ICONS,
  PLATFORM_TRANSLATION_KEYS,
} from 'lumen/utils/constants/general-constants';
import {
  SUBTYPE_VALUES_KEY_CANCELLATION_POLICY,
  SUBTYPE_VALUES_KEY_STRATEGY,
  TYPE_ID_SEMI_FLEX,
} from 'lumen/utils/constants/rate-strategy-constants';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function getSubtypeOptionIcons({
  // @ts-expect-error: Incorrect type
  isMobileType,
  // @ts-expect-error: Incorrect type
  isMealtypeType,
  // @ts-expect-error: Incorrect type
  label,
  // @ts-expect-error: Incorrect type
  isPosBookingSubtype,
  // @ts-expect-error: Incorrect type
  isPosExpediaSubtype,
}) {
  if (isMobileType) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    return { iconBrand: label };
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  if (isMealtypeType && MEALTYPE_ICONS[label]) {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    return { icon: MEALTYPE_ICONS[label] };
  }

  if (isPosBookingSubtype) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    return { iconBrand: OTA_IDS.BOOKINGDOTCOM, iconFlag: label.toLowerCase() };
  }

  if (isPosExpediaSubtype) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    return { iconBrand: OTA_IDS.EXPEDIA, iconFlag: label.toLowerCase() };
  }

  return {};
}

interface HotelStrategyTabEvolutionGraphSignature {
  Args: {
    params: unknown;
  };
}

export default class HotelStrategyTabEvolutionGraph extends Component<HotelStrategyTabEvolutionGraphSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  // @ts-expect-error: Incorrect type
  countriesUtil = new Countries(getOwner(this));

  @computed('args.params.height')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get correctedHeight() {
    // Return the height minus the height of the filter bar
    // @ts-expect-error: Incorrect type
    return this.args.params.height - 40;
  }

  @computed('args.params.width')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get correctedWidth() {
    // @ts-expect-error: Incorrect type
    return Math.floor(this.args.params.width * 0.9);
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get countries() {
    if (this.isPosType) {
      // @ts-expect-error: Incorrect type
      return this.hotelAndMarketService.hotel.rsPosCountries.map((country) => ({
        id: country,
        iconFlag: country.toLowerCase(),
        label: this.countriesUtil.getName(country.toUpperCase()),
      }));
    }

    return [];
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get entities() {
    // For Semi-flex both strategies and cancellation policy data can be shown
    // @ts-expect-error: Incorrect type
    if (this.args.params.subtypeId === TYPE_ID_SEMI_FLEX) {
      return [
        {
          id: SUBTYPE_VALUES_KEY_STRATEGY,
          label: this.intl.t('strategy.metric_block.discount'),
        },
        {
          id: SUBTYPE_VALUES_KEY_CANCELLATION_POLICY,
          label: this.intl.t('strategy.metric_block.cutoff_policy'),
        },
      ];
    }

    return [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMobileType() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.args.params.subtypes[0].isMobileType;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPosType() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.args.params.subtypes[0].isPosType;
  }

  // Should the left arrow be visible
  @computed('args.params.offset')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get leftLimit() {
    // @ts-expect-error: Incorrect type
    return this.args.params.offset < 48;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get otasAvailable() {
    return [
      {
        id: OTA_IDS.BOOKINGDOTCOM,
        label: 'Booking.com',
        iconBrand: OTA_IDS.BOOKINGDOTCOM,
      },
      {
        id: OTA_IDS.EXPEDIA,
        label: 'Expedia',
        iconBrand: OTA_IDS.EXPEDIA,
      },
    ];
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get platforms() {
    if (this.isMobileType) {
      return [
        {
          id: PLATFORMS.PLATFORM_MOBILE_WEBSITE,
          label: this.intl.t(
            `${PLATFORM_TRANSLATION_KEYS[PLATFORMS.PLATFORM_MOBILE_WEBSITE]}`,
          ),
        },
        {
          id: PLATFORMS.PLATFORM_MOBILE_APP,
          label: this.intl.t(
            `${PLATFORM_TRANSLATION_KEYS[PLATFORMS.PLATFORM_MOBILE_APP]}`,
          ),
        },
      ];
    }

    return [];
  }

  // Should the right arrow be visible
  @computed('args.params.offset')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rightLimit() {
    // @ts-expect-error: Incorrect type
    return this.args.params.offset !== 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedCountry() {
    return this.countries.find(
      // @ts-expect-error: Incorrect type
      (country) => country.id === this.args.params.countryId,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedEntity() {
    return this.entities.find(
      // @ts-expect-error: Incorrect type
      (entity) => entity.id === this.args.params.entityId,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedOta() {
    // @ts-expect-error: Incorrect type
    return this.otasAvailable.find((ota) => ota.id === this.args.params.otaId);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedPlatform() {
    return this.platforms.find(
      // @ts-expect-error: Incorrect type
      (platform) => platform.id === this.args.params.platformId,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedSubtype() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.subtypeOptions.find(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (subtypeOption) => subtypeOption.id === this.args.params.subtypeId,
    );
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get subtypeOptions() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.args.params.subtypes.map((rssubtype) => {
      const baseOption = {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        id: rssubtype.name,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        subtype: rssubtype,
      };

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (rssubtype.isMemberRatesType) {
        return {
          ...baseOption,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          label: this.intl.t(`general.member_rates.${rssubtype.label}`),
        };
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (rssubtype.isCancellationPolicyType) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (rssubtype.isBestFlexType || rssubtype.isSemiFlexType) {
          return {
            ...baseOption,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            icon: rssubtype.rstype.icon,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            label: rssubtype.rstype.formattedTypeName,
          };
        }

        return {
          ...baseOption,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          label: rssubtype.rstype.formattedTypeName,
        };
      }

      return {
        ...baseOption,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        label: rssubtype.formattedLabel[SUBTYPE_VALUES_KEY_STRATEGY],
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        ...getSubtypeOptionIcons(rssubtype),
      };
    });
  }

  @action
  decreaseOffset(): void {
    // @ts-expect-error: Incorrect type
    const { offset } = this.args.params;
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.args.params.updateOffset(offset + 8);
  }

  @action
  increaseOffset(): void {
    // @ts-expect-error: Incorrect type
    const { offset } = this.args.params;
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.args.params.updateOffset(offset - 8);
  }

  <template>
    <GeneralModalFilterBar class="flex justify-between">
      <div class="flex items-center justify-start gap-200">
        {{#if (or this.isMobileType this.isPosType)}}
          <PrismSelectInput
            @isSmall={{true}}
            {{! @glint-expect-error: Incorrect type }}
            @onChange={{@params.chooseOta}}
            @options={{this.otasAvailable}}
            @value={{this.selectedOta}}
          />

          {{#if this.platforms}}
            <PrismSelectInput
              @isSmall={{true}}
              {{! @glint-expect-error: Incorrect type }}
              @onChange={{@params.choosePlatform}}
              @options={{this.platforms}}
              @value={{this.selectedPlatform}}
            />
          {{/if}}

          {{#if this.countries}}
            <PrismSelectInput
              @isSmall={{true}}
              {{! @glint-expect-error: Incorrect type }}
              @onChange={{@params.chooseCountry}}
              {{! @glint-expect-error: Incorrect type }}
              @options={{this.countries}}
              {{! @glint-expect-error: Incorrect type }}
              @value={{this.selectedCountry}}
            />
          {{/if}}

        {{else}}
          <PrismSelectInput
            @isSmall={{true}}
            {{! @glint-expect-error: Incorrect type }}
            @onChange={{@params.chooseSubtype}}
            @options={{this.subtypeOptions}}
            @value={{this.selectedSubtype}}
          />
        {{/if}}

        {{#if this.entities.length}}
          <PrismSelectInput
            @isSmall={{true}}
            {{! @glint-expect-error: Incorrect type }}
            @onChange={{@params.chooseEntity}}
            @options={{this.entities}}
            @value={{this.selectedEntity}}
          />
        {{/if}}
      </div>
    </GeneralModalFilterBar>

    <div class="strategy-evolution-container">
      <div class="left-side-container">
        {{#if this.leftLimit}}
          <div class="offset-arrow-btn">
            <PrismButton
              @buttonType="secondary"
              @icon="arrow-left"
              @onClick={{this.decreaseOffset}}
            />
          </div>
        {{/if}}
      </div>
      <div class="rse-curve">
        <HotelStrategyChartsChartEvolution
          @containerHeight={{this.correctedHeight}}
          {{! @glint-expect-error: Incorrect type }}
          @correctedWidth={{this.correctedWidth}}
          {{! @glint-expect-error: Incorrect type }}
          @isLoading={{this.model.loading}}
          @params={{@params}}
        />
      </div>
      <div class="right-side-container">
        {{#if this.rightLimit}}
          <div class="offset-arrow-btn">
            <PrismButton
              @buttonType="secondary"
              @icon="arrow-right"
              @onClick={{this.increaseOffset}}
            />
          </div>
        {{/if}}
      </div>
    </div>
  </template>
}
